import React, { useEffect, useState } from 'react';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { firestore } from './FirebaseConfig';

const RefundData = () => {
  const [refundData, setRefundData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const querySnapshot = await getDocs(collection(firestore, 'Refund'));
        const data = [];
        querySnapshot.forEach((doc) => {
          data.push({ id: doc.id, ...doc.data() });
        });
        setRefundData(data);
      } catch (error) {
        console.error('Error fetching refund data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDelete = async (id) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this refund record?');
    if (confirmDelete) {
      try {
        await deleteDoc(doc(firestore, 'Refund', id));
        setRefundData(refundData.filter((refund) => refund.id !== id));
      } catch (error) {
        console.error('Error deleting refund data:', error);
      }
    }
  };

  // Filter data based on search term
  const filteredData = refundData.filter(refund => 
    refund.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    refund.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    refund.mobile.includes(searchTerm) ||
    refund.applicationNo.includes(searchTerm)
  );

  // Sorting functionality
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return filteredData;
    
    return [...filteredData].sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig]);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-4">
      <div className="row">
        <div className="col-12">
          <div className="card shadow-lg border-0">
            <div className="card-header bg-danger text-white d-flex justify-content-between align-items-center">
              <div>
                <h2 className="h4 mb-0">Refund Management</h2>
                <p className="mb-0 opacity-75">Manage all refund requests in one place</p>
              </div>
              <div className="w-25">
                <div className="input-group input-group-sm">
                  <span className="input-group-text bg-white">
                    <i className="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search refunds..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive" style={{ maxHeight: '70vh' }}>
                <table className="table table-hover align-middle mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th scope="col" className="ps-4">#</th>
                      <th 
                        scope="col" 
                        className="cursor-pointer"
                        onClick={() => handleSort('name')}
                      >
                        Name {sortConfig.key === 'name' && (
                          <i className={`bi bi-chevron-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                        )}
                      </th>
                      <th scope="col">Email</th>
                      <th scope="col">Mobile</th>
                      <th scope="col">Application No</th>
                      <th 
                        scope="col"
                        className="cursor-pointer"
                        onClick={() => handleSort('amount')}
                      >
                        Amount {sortConfig.key === 'amount' && (
                          <i className={`bi bi-chevron-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                        )}
                      </th>
                      <th 
                        scope="col"
                        className="cursor-pointer"
                        onClick={() => handleSort('timestamp')}
                      >
                        Date {sortConfig.key === 'timestamp' && (
                          <i className={`bi bi-chevron-${sortConfig.direction === 'asc' ? 'up' : 'down'}`}></i>
                        )}
                      </th>
                      <th scope="col" className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedData.length > 0 ? (
                      sortedData.map((refund, index) => (
                        <tr key={refund.id} className={index % 2 === 0 ? 'bg-light' : ''}>
                          <td className="ps-4 fw-bold">{index + 1}</td>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center me-2" style={{ width: '36px', height: '36px' }}>
                                <span className="text-white fw-bold">{refund.name.charAt(0)}</span>
                              </div>
                              <div>{refund.name}</div>
                            </div>
                          </td>
                          <td>
                            <a href={`mailto:${refund.email}`} className="text-decoration-none">
                              {refund.email}
                            </a>
                          </td>
                          <td>
                            <a href={`tel:${refund.mobile}`} className="text-decoration-none">
                              {refund.mobile}
                            </a>
                          </td>
                          <td>
                            <span className="badge bg-info text-dark">{refund.applicationNo}</span>
                          </td>
                          <td className="fw-bold text-success">{formatCurrency(refund.amount)}</td>
                          <td>
                            <div className="text-nowrap">
                              {refund.timestamp ? refund.timestamp.toDate().toLocaleDateString() : 'N/A'}
                              <br />
                              <small className="text-muted">
                                {refund.timestamp ? refund.timestamp.toDate().toLocaleTimeString() : ''}
                              </small>
                            </div>
                          </td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => handleDelete(refund.id)}
                              title="Delete refund request"
                            >
                              <i className="bi bi-trash"></i> Delete
                            </button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center py-4">
                          {refundData.length === 0 ? 'No refund requests found.' : 'No matching refund requests found.'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-footer bg-light d-flex justify-content-between align-items-center">
              <small className="text-muted">
                Showing {sortedData.length} of {refundData.length} refund requests
              </small>
              <div>
                <span className="badge bg-primary me-2">
                  Total Amount: {formatCurrency(refundData.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0))}
                </span>
                <small className="text-muted">
                  Last updated: {new Date().toLocaleTimeString()}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundData;